import {
  Accordion,
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  Menu,
  Skeleton,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { Fragment, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addIcon, DummyImage, removeIcon, stepBackColor, stepForwardColor } from '../../assets';
import { toastError } from '../../config/Helper';
import { AxiosResponse, isAxiosError } from 'axios';
import { IBaseResponseError } from '../../typescript/interfaces/common.interface';
import { useGlobalContext } from '../../context/global-context';
import {
  cardItemDeleteService,
  cardItemUpdateService,
  cartItemService,
} from '../../services/cart-item/cart-item.service';
import { useGetExtraItems } from '../../services/extra-items/extra-items.service';
import { ICartItemServiceResponse, IMenuItem, IVariant } from '../../typescript/interfaces/cart-item.interface';
import { IBaseResponse } from '../../typescript/interfaces/common.interface';
import GreenDot from '../RestaurantDetails/GreenDot';
import RedDot from '../RestaurantDetails/RedDot';
import YellowDot from '../RestaurantDetails/YellowDot';
import { useAddToCartRefactoredStyles } from './style';
import { useMutation } from '@tanstack/react-query';

interface ICartItemsPayload {
  _id?: number;
  restaurant_id?: number;
  menu_item_id?: number;
  quantity: number;
  addon_level_ids: number[];
  note: string;
}

type Action =
  | {
      type: 'FOOD_ITEM_INCREMENT' | 'FOOD_ITEM_DECREMENT';
      payload: {
        quantity: number;
      };
    }
  | {
      type: 'INITIAL_PRICE_CALCULATE';
      payload: {
        unitPrice: number;
      };
    }
  | {
      type: 'ADD_EXTRA_ITEM';
      payload: {
        amount: number;
        index: number;
      };
    }
  | {
      type: 'REMOVE_EXTRA_ITEM';
      payload: {
        amount: number;
        index: number;
      };
    }
  | {
      type: 'CHANGE_EXTRA_ITEM_WHILE_CHECKED';
      payload: {
        amount: number;
      };
    };

type TotalPriceState = {
  unitPrice: number;
  totalPrice: number;
  quantity: number;
  totalPriceWithoutAddedItem: number;
  addedItems: Set<number>;
};

const initialTotalPriceState: TotalPriceState = {
  unitPrice: 0,
  totalPrice: 0,
  quantity: 0,
  totalPriceWithoutAddedItem: 0,
  addedItems: new Set(),
};

class StateUpdater<T> {
  private readonly state: T;

  constructor(state: T) {
    this.state = { ...state };
  }

  update<K extends keyof T & string>(key: K, value: T[K]): this {
    if (this.state[key] !== value) {
      this.state[key] = value;
    }
    return this;
  }

  getState(): T {
    return { ...this.state };
  }
}

const reducer = (state: TotalPriceState, action: Action): TotalPriceState => {
  const stateUpdater = new StateUpdater(state);

  switch (action.type) {
    case 'INITIAL_PRICE_CALCULATE': {
      return stateUpdater
        .update('totalPrice', action.payload.unitPrice)
        .update('quantity', 1)
        .update('totalPriceWithoutAddedItem', action.payload.unitPrice)
        .update('unitPrice', action.payload.unitPrice)
        .getState();
    }

    case 'FOOD_ITEM_INCREMENT': {
      const newTotalPrice = state.totalPrice + state.unitPrice * action.payload.quantity;
      const newQuantity = state.quantity + action.payload.quantity;
      return stateUpdater
        .update('totalPrice', newTotalPrice)
        .update('totalPriceWithoutAddedItem', newTotalPrice)
        .update('quantity', newQuantity)
        .getState();
    }

    case 'FOOD_ITEM_DECREMENT': {
      const newTotalPrice = state.totalPrice - state.unitPrice * action.payload.quantity;
      const newQuantity = state.quantity === 1 ? state.quantity : state.quantity - action.payload.quantity;
      return stateUpdater
        .update('totalPrice', newTotalPrice)
        .update('totalPriceWithoutAddedItem', newTotalPrice)
        .update('quantity', newQuantity)
        .getState();
    }

    case 'ADD_EXTRA_ITEM': {
      const newTotalPrice = state.totalPrice + action.payload.amount;
      const previousSet = state.addedItems;

      const s = new Set(previousSet);
      s.add(action.payload.index);
      return stateUpdater.update('totalPrice', newTotalPrice).update('addedItems', s).getState();
    }

    case 'REMOVE_EXTRA_ITEM': {
      const newTotalPrice = state.totalPrice - action.payload.amount;

      const previousSet = state.addedItems;
      const s = new Set(previousSet);
      s.delete(action.payload.index);
      return stateUpdater.update('totalPrice', newTotalPrice).update('addedItems', s).getState();
    }

    case 'CHANGE_EXTRA_ITEM_WHILE_CHECKED': {
      return stateUpdater.update('totalPrice', state.totalPriceWithoutAddedItem + action.payload.amount).getState();
    }
  }
};

interface INewItemAcordionProps {
  restaurantInfo: {
    restaurantId: number;
    restaurantName: string;
    rating: number;
    ratingCount: number;
    openingHours: string;
    fullAddress: string;
  };
  foodInfo: IMenuItem;
  variants?: IVariant[];
  type?: 'regular' | 'loading';
  _addToCardIsLoading: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  disabled?: boolean;
}

const NewItemAcordion = ({
  restaurantInfo,
  foodInfo,
  variants: variantsCart,
  _addToCardIsLoading,
  disabled,
}: INewItemAcordionProps) => {
  const { classes, cx } = useAddToCartRefactoredStyles();
  const [accordionOpen, setAccordionOpen] = useState<number>(0);
  const [variants, setVariants] = useState<IVariant[]>([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newCustomizationMode, setNewCustomizationMode] = useState(false);
  const [lastCustomization, setLastCustomization] = useState<{
    _id: number;
    addon_level_ids: number[];
    quantity: number;
  } | null>(null);

  useEffect(() => {
    if (variantsCart?.length) {
      setVariants([...variantsCart]);
    }
  }, [variantsCart]);

  // const variants = useSelector((state: TRootState) => state.cartVariant.variants);
  // const appDispatch = useDispatch();

  // appDispatch(setVariants());

  const { data, isLoading, isSuccess, refetch } = useGetExtraItems({
    menuItemId: accordionOpen,
    enabledIf: accordionOpen ? true : false,
  });
  const { cookies, setCookie } = useGlobalContext();
  const navigate = useNavigate();
  const [totalPrice, dispatch] = useReducer(reducer, initialTotalPriceState);
  const [requestToChefNote, setRequestToChefNote] = useState('');
  const [currVariantId, setCurrVariantId] = useState(-1);

  const _cartItemService = useMutation({
    mutationKey: ['card-item'],
    mutationFn: cartItemService,
  });
  const _cardItemUpdateService = useMutation({
    mutationKey: ['card-item-update'],
    mutationFn: cardItemUpdateService,
    onSuccess: () => {
      setCurrVariantId(-1);
    },
  });

  const _cardItemDeleteService = useMutation({
    mutationKey: ['card-item-delete'],
    mutationFn: cardItemDeleteService,
    onSuccess: () => {
      setCurrVariantId(-1);
    },
  });


  useEffect(() => {
    dispatch({ type: 'INITIAL_PRICE_CALCULATE', payload: { unitPrice: foodInfo.price } });
  }, []);

  // useEffect(() => {
  //   if (_variants[0]) {
  //     _variants[1](_variants[0]);
  //   }
  // }, [variants]);

  type ExtraItem = Record<number, { currentValue: number; minValue: number; id: number }>;

  const [record, setRecord] = useState<ExtraItem>({});

  useEffect(() => {
    if (data) {
      let obj: ExtraItem = {};
      if (data) {
        let obj: ExtraItem = {};

        data?.item_choices.forEach((value, index) => {
          // const maximumValue = data.item_choices[index].addon_levels.length;
          const id = data.item_choices[index].addon_levels[0].addon_level_id;
          const findDefaultAddOnIndex = data.item_choices[index].addon_levels?.findIndex((_ele) => _ele?.is_default);
          obj = {
            ...obj,
            [index]: {
              id: id,
              currentValue: data.item_choices[index]?.addon_required ? findDefaultAddOnIndex : 0,
              // maxValue: data.item_choices[index]?.addon_required ? maximumValue : maximumValue + 1,
              minValue: data.item_choices[index]?.addon_required ? 0 : -1,
            },
          };
        });

        setRecord(obj);
      }
    }
  }, [data]);

  const handleCheckBoxClick = ({
    e,
    index,
    amount,
  }: {
    e: React.ChangeEvent<HTMLInputElement>;
    index: number;
    amount: number;
  }) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      dispatch({
        type: 'ADD_EXTRA_ITEM',
        payload: {
          amount: amount,
          index: index,
        },
      });
    } else {
      dispatch({
        type: 'REMOVE_EXTRA_ITEM',
        payload: {
          amount: amount,
          index: index,
        },
      });
    }
  };

  const [addToCardIsLoading, setAddToCardIsLoading] = _addToCardIsLoading;

  // ------------------------

  // const repeatLastCustomization = async () => {
  //   if (!lastCustomization || addToCardIsLoading) return;

  //   await handleUpdate(
  //     {
  //       _id: lastCustomization?._id,
  //       addon_level_ids: lastCustomization.addon_level_ids,
  //       quantity: lastCustomization.quantity,
  //     },
  //     'repeat-same',
  //   );

  //   setShowAddMenu(false);
  // };

  const startNewCustomization = () => {
    setNewCustomizationMode(true);
    setShowAddMenu(false);
  };

  // --------------------------------

  const buildAddonMap = () => {
    const map: IVariant['addons'] = {};
    let addonsTotal = 0;

    Object.entries(record).forEach(([indexStr, val]) => {
      const index = Number(indexStr);
      const level = data?.item_choices[index]?.addon_levels[val.currentValue];

      if (level && level.addon_level_id) {
        map[index] = {
          addonId: level.addon_level_id,
          name: `${data.item_choices[index].name} (${level.name})`,
          amount: level.amount,
        };
        addonsTotal += level.amount;
      }
    });

    return { map, addonsTotal };
  };

  const addVariant = (_id: number, overrideAddonIds?: number[], overrideQty?: number, shouldAppend?: boolean) => {
    const { map, addonsTotal } = buildAddonMap();
    const unitPrice = foodInfo.price + addonsTotal;

    const addonIds = overrideAddonIds ?? Object.values(map).map((a) => a.addonId);

    const qty = overrideQty ?? totalPrice.quantity;

    setVariants((prev) => {
      const existingIndex = prev.findIndex((v) => JSON.stringify(v.addon_level_ids) === JSON.stringify(addonIds));

      let previousCartItemPrice = +(cookies['cart-item-price'] ?? 0);

      if (existingIndex !== -1) {
        const updated = [...prev];

        updated[existingIndex].quantity = shouldAppend ? updated[existingIndex].quantity + qty : qty;

        previousCartItemPrice += updated[existingIndex].unitPrice * (shouldAppend ? qty : 1);

        setCookie('cart-item-price', previousCartItemPrice);

        // appDispatch(
        //   setVariants({
        //     [foodInfo?.menu_item_id]: updated,
        //   }),
        // );

        return updated;
      }

      previousCartItemPrice += unitPrice * (overrideQty ?? 0);

      setCookie('cart-item-price', previousCartItemPrice);

      const currState = [
        ...prev,
        {
          id: _id ?? Math.random() * 10000,
          quantity: qty,
          addon_level_ids: addonIds,
          addons: map,
          unitPrice,
          addonsTotal,
        },
      ];

      // appDispatch(
      //   setVariants({
      //     [foodInfo?.menu_item_id]: currState,
      //   }),
      // );

      return currState;
    });
  };

  const deleteVariant = (id: number, overrideQty: number, shouldDelete?: true) => {
    let previousCartItemPrice = +(cookies['cart-item-price'] ?? 0);
    if (!overrideQty) {
      return setVariants((prev) => {
        const currState = prev.filter((x) => {
          if (x.id === id) previousCartItemPrice -= x.unitPrice * (shouldDelete ? x.quantity : 1);
          setCookie('cart-item-price', previousCartItemPrice);
          return x.id !== id;
        });

        // appDispatch(
        //   setVariants({
        //     [foodInfo?.menu_item_id]: currState,
        //   }),
        // );

        return currState;
      });
    }

    setVariants((prev) => {
      const currState = prev.map((x) => {
        if (x.id === id) {
          previousCartItemPrice -= x.unitPrice;
          setCookie('cart-item-price', previousCartItemPrice);
          return { ...x, quantity: overrideQty };
        }

        return x;
      });

      // appDispatch(
      //   setVariants({
      //     [foodInfo?.menu_item_id]: currState,
      //   }),
      // );

      return currState;
    });
  };

  const handleUpdate = async (
    action: 'add' | 'increase' | 'decrease' | 'delete',
    override?: { _id: number; addon_level_ids?: number[]; quantity?: number } | null,
  ) => {
    setAddToCardIsLoading(true);
    if (override?._id) setCurrVariantId(override?._id);

    // const addon_level_ids: number[] = [];
    // let cartItemId = override?._id ?? -1;

    // Object.entries(record).forEach((data) => {
    //   if (totalPrice.addedItems.has(+data[0])) {
    //     addon_level_ids.push(data[1].id);
    //   }
    // });

    const addon_level_ids = Object.values(record)
      .map((r) => r.id)
      .filter(Boolean);


    const finalAddonIds = override?.addon_level_ids ?? addon_level_ids;
    let finalQuantity = override?.quantity ?? totalPrice.quantity;

    const existingIndex = variants.findIndex(
      (v) => JSON.stringify(v.addon_level_ids) === JSON.stringify(finalAddonIds),
    );

    let cartItemId = override?._id ?? variants[existingIndex]?.id ?? -1;

    let objectToSend: ICartItemsPayload;

    if (override || (action === 'add' && existingIndex !== -1)) {
      objectToSend = {
        _id: cartItemId,
        quantity: finalQuantity,
        addon_level_ids: finalAddonIds,
        note: requestToChefNote,
      };
    } else {
      objectToSend = {
        restaurant_id: foodInfo?.restaurant_id,
        menu_item_id: foodInfo?.menu_item_id,
        quantity: finalQuantity,
        addon_level_ids: finalAddonIds,
        note: requestToChefNote,
      };
    }

    if (action === 'add' && existingIndex !== -1) {
      objectToSend = { ...objectToSend, quantity: variants[existingIndex]?.quantity + objectToSend?.quantity };
    }

    try {
      let response: ICartItemServiceResponse | IBaseResponse;

      if (finalQuantity && action !== 'delete') {
        response = (await (override || (action === 'add' && existingIndex !== -1)
          ? _cardItemUpdateService
          : _cartItemService
        ).mutateAsync(objectToSend)) as ICartItemServiceResponse;
      } else {
        response = (await _cardItemDeleteService.mutateAsync({
          _id: cartItemId,
        })) as IBaseResponse;
      }

      setAddToCardIsLoading(false);
      refetch();
      let cartItemCount = +(cookies['cart-item-count'] ?? 0);
      let previousCartItemPrice = cookies['cart-item-price'];
      const previousRestaurantIdIfAny = cookies['current-restaurant-id'];

      if ('data' in response) cartItemId = response?.data?.cart_item_id;
      // let totalItemCount = 'data' in response ? +response?.data?.items_count : 0;
      // let intialValue = -1;
      // if (action && ['add', 'repeat-same'].includes(action)) intialValue = 1;
      // if (action === 'delete' && override?.quantity) intialValue = -override.quantity;
      // if ('data' in response) console.log(variants, 'again variants', response?.data?.items_count);

      // if (override && action) {
      //   totalItemCount = variants?.reduce((prev, curr) => {
      //     return prev + curr?.quantity;
      //   }, intialValue);
      // }
      if (action === 'add') cartItemCount += finalQuantity;
      if (action === 'increase') cartItemCount++;
      if (action === 'decrease') cartItemCount--;
      if (action === 'delete') cartItemCount -= finalQuantity;

      if (!override && 'data' in response) {
        // ✅ snapshot for "Repeat same"
        setLastCustomization({
          _id: response?.data?.cart_item_id,
          addon_level_ids: finalAddonIds,
          quantity: finalQuantity,
        });
      }

      if (previousRestaurantIdIfAny) {
        /**
         * If we already have added an item from a restaurant
         */
        if (+previousRestaurantIdIfAny !== +restaurantInfo?.restaurantId) {
          /**
           * If the item we are trying to add is not from the same restaurant
           */

          setCookie('current-restaurant-id', restaurantInfo?.restaurantId);
          setCookie('cart-item-count', cartItemCount);
          setCookie('cart-item-price', +totalPrice.totalPrice);
        } else {
          /**
           * If the item we are trying to add is from the same restaurant
           */

          setCookie('cart-item-count', parseInt(cartItemCount.toString()));
          setCookie('cart-item-price', parseFloat(previousCartItemPrice) + totalPrice.totalPrice);
        }
      } else {
        /**
         * If this is the first time we are adding an item to the cart
         */
        setCookie('current-restaurant-id', foodInfo?.restaurant_id);
        setCookie('cart-item-count', cartItemCount);
        setCookie('cart-item-price', totalPrice.totalPrice);
      }

      // navigate(ROUTES.DETAILS + `/${foodInfo.restaurant_id}`, {
      //   state: {
      //     restaurantInfo: restaurantInfo,
      //   },
      // });
      if (['add', 'increase'].includes(action))
        addVariant(cartItemId, finalAddonIds, finalQuantity, action === 'add' && existingIndex !== -1);
      if (action === 'decrease') deleteVariant(cartItemId, finalQuantity);
      if (action === 'delete') deleteVariant(cartItemId, 0, true);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const response = error.response as AxiosResponse<IBaseResponseError> | undefined;
        const data = response?.data;
        const hasApiError = data?.data?.error_type && data?.data?.error_message;
        toastError(hasApiError ? data : data?.message);
      }
      setAddToCardIsLoading(false);
    }
  };

  const handleFoodQuantityChange = (type: 'increase' | 'decrease', byAmount: number) => {
    switch (type) {
      case 'increase':
        dispatch({
          type: 'FOOD_ITEM_INCREMENT',
          payload: {
            quantity: byAmount,
          },
        });

        break;
      case 'decrease':
        dispatch({
          type: 'FOOD_ITEM_DECREMENT',
          payload: {
            quantity: byAmount,
          },
        });
        break;
    }
  };

  const handleChange = (index: number, type: 'increase' | 'decrease', price: number, id: number) => {
    if (totalPrice.addedItems.has(index)) {
      dispatch({
        type: 'CHANGE_EXTRA_ITEM_WHILE_CHECKED',
        payload: {
          amount: price,
        },
      });
    }

    switch (type) {
      case 'increase':
        setRecord((prev) => {
          // const maximumValue = prev[index].maxValue;
          const currentValue = prev[index].currentValue;

          return {
            ...prev,
            [index]: {
              ...prev[index],
              id: id,
              currentValue: currentValue + 1,
            },
          };
        });

        break;
      case 'decrease':
        console.log('decrease');
        setRecord((prev) => {
          const currentValue = prev[index].currentValue;
          // const minValue = prev[index].minValue;
          return {
            ...prev,
            [index]: { ...prev[index], id: id, currentValue: currentValue - 1 },
          };
        });
        break;
    }
  };

  console.log('item_choices', record, data?.item_choices);

  return (
    <>
      {/* <Container> */}
      <Accordion
        // defaultValue={String(foodInfo.menu_item_id)}
        // onChange={disabled ? undefined : () => setAccordionOpen(foodInfo.menu_item_id)}
        chevron={null}
        styles={(theme) => ({
          item: {
            border: 'none',
          },
          control: {
            padding: 0,
            ':hover': {
              background: 'transparent',
            },
          },
          chevron: {
            display: 'none',
          },

          content: {
            padding: 0,
          },

          label: {
            padding: 0,
          },
        })}>
        <Accordion.Item value={String(foodInfo.menu_item_id)} onClick={() => setAccordionOpen(foodInfo.menu_item_id)}>
          <Accordion.Control disabled={disabled}>
            <Box
              style={{
                padding: 8,
                border: '0.0625rem solid #dee2e6',
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderRadius: 20,
                background: 'white',
              }}>
              <Grid p={0} m={0}>
                <Grid.Col className={(classes.genericGridCol, classes.firstGridCol)}>
                  <Image src={foodInfo.image_url || DummyImage} radius={12} fit='cover' width={'100%'} height={75} />
                </Grid.Col>
                <Grid.Col className={(classes.genericGridCol, classes.secondGridCol)}>
                  <Flex direction='column' gap={16} justify='center' h={'100%'}>
                    <Text fz={14} fw={500} lh={1}>
                      {foodInfo.name}
                    </Text>

                    <Flex gap={16}>
                      <Flex gap={4} align='center'>
                        {foodInfo.diet.diet_id === 3 ? <RedDot /> : <GreenDot />}
                        <Text fz={12} lh={1} fw={500} c='dimmed'>
                          {foodInfo.diet.name}
                        </Text>
                      </Flex>

                      <Flex gap={4} align='center'>
                        <YellowDot />
                        <Text fz={12} lh={1} fw={500} c='dimmed'>
                          {foodInfo.calories} Cal
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Grid.Col>
                <Grid.Col className={(classes.genericGridCol, classes.thirdGridCol)}>
                  <Flex direction='column' gap={16} justify='center' align='center' h='100%'>
                    <Text fz={18} fw={500} c='red' lh={1} ta='center'>
                      ${foodInfo.price.toFixed(2)}
                    </Text>
                    <Box
                      style={{
                        border: '1px solid #D61D4F',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 4,
                        width: '100%',
                        borderRadius: 8,
                      }}>
                      <Text fz={11} lh={1} c='magenta.9' fw={600}>
                        Details
                      </Text>
                    </Box>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          </Accordion.Control>

          <Accordion.Panel>
            <Divider mt={12} />
            <Text fz={16} fw={600} mt={8}>
              Item Description
            </Text>
            <Text fz={13} fw={400}>
              {foodInfo.description}
            </Text>
            {/* {(variants.length === 0 || newCustomizationMode) && ( */}
            <Table mt={16} className={classes.table}>
              {data?.item_choices?.some((item) => item?.addon_levels?.length > 0) ? (
                <thead>
                  <tr>
                    {/* <th></th> */}
                    <th>
                      <Text>Item</Text>
                    </th>
                    <th>
                      <Text ta='center'>Unit Price</Text>
                    </th>
                    <th>
                      <Text ta='center'>Level</Text>
                    </th>
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {isLoading &&
                  Array(1)
                    .fill(0)
                    .map((_, index) => (
                      <tr key={index} style={{ height: 45 }}>
                        {/* <td></td> */}
                        <td>
                          <Skeleton h={16} w='32%' />
                        </td>
                        <td>
                          <Skeleton h={16} w='36%' mx='auto' />
                        </td>
                        <td>
                          <Box h={'100%'}>
                            <Skeleton h={16} w='88%' mx='auto' />
                          </Box>
                        </td>
                      </tr>
                    ))}

                {isSuccess &&
                  data?.item_choices?.map((d, index: number) => (
                    <tr key={index}>
                      {/* <td>
                        <Checkbox
                          onChange={(e) => {
                            handleCheckBoxClick({
                              e,
                              index,
                              amount: data.item_choices[index].addon_levels[record[index].currentValue].amount,
                            });
                          }}
                          radius='xs'
                          size='xs'
                          styles={(theme) => ({
                            input: {
                              cursor: 'pointer',
                            },
                          })}
                        />
                      </td> */}
                      <td>
                        <Text lh={1}>{d?.name}</Text>
                      </td>
                      <td>
                        <Text lh={1} ta='center'>
                          {!!data.item_choices[index]?.addon_levels[record[index]?.currentValue]
                            ? `$ ${data.item_choices[index]?.addon_levels[record[index]?.currentValue]?.amount?.toFixed(
                                2,
                              )}`
                            : 'N/A'}
                        </Text>
                      </td>
                      <td style={{ display: 'flex', justifyContent: 'center' }}>
                        <Grid p={0} m={0} w='100%'>
                          <Grid.Col
                            className={cx(classes.extraFoodChangerGenericGridCol, classes.extraFoodChangerFirstGridCol)}
                            style={{ display: 'flex', justifyContent: 'center' }}>
                            <ActionIcon
                              disabled={record[index]?.currentValue - 1 < record[index]?.minValue}
                              sx={(theme) => ({
                                '&[data-disabled]': {
                                  pointerEvents: 'all',
                                  backgroundColor: 'transparent',
                                  border: 'none',

                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                },

                                '&:hover': {
                                  backgroundColor: theme.colors.orange[0],
                                  borderRadius: '50%',
                                },
                              })}
                              onClick={() => {
                                handleChange(
                                  index,
                                  'decrease',
                                  data?.item_choices[index].addon_levels?.[record[index].currentValue - 1]?.amount ?? 0,
                                  data?.item_choices[index].addon_levels?.[record[index].currentValue - 1]
                                    ?.addon_level_id ?? 0,
                                );
                              }}>
                              <Box>
                                <Image src={stepBackColor} />
                              </Box>
                            </ActionIcon>
                          </Grid.Col>
                          <Grid.Col
                            className={cx(
                              classes.extraFoodChangerGenericGridCol,
                              classes.extraFoodChangerSecondGridCol,
                            )}
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {!!data?.item_choices[index]?.addon_levels[record[index]?.currentValue]
                              ? data?.item_choices[index]?.addon_levels[record[index]?.currentValue]?.name
                              : 'none'}
                          </Grid.Col>
                          <Grid.Col
                            className={cx(classes.extraFoodChangerGenericGridCol, classes.extraFoodChangerThirdGridCol)}
                            style={{ display: 'flex', justifyContent: 'center' }}>
                            <ActionIcon
                              disabled={
                                record[index]?.currentValue === data?.item_choices?.[index]?.addon_levels?.length - 1
                              }
                              sx={(theme) => ({
                                '&[data-disabled]': {
                                  pointerEvents: 'all',
                                  backgroundColor: 'transparent',
                                  border: 'none',

                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                },

                                '&:hover': {
                                  backgroundColor: theme.colors.orange[0],
                                  borderRadius: '50%',
                                },
                              })}
                              onClick={() =>
                                handleChange(
                                  index,
                                  'increase',
                                  data?.item_choices[index].addon_levels[record[index].currentValue + 1]?.amount ?? 0,
                                  data?.item_choices[index].addon_levels[record[index].currentValue + 1]
                                    ?.addon_level_id ?? 0,
                                )
                              }>
                              <Box>
                                <Image src={stepForwardColor} />
                              </Box>
                            </ActionIcon>
                          </Grid.Col>
                        </Grid>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {/* )} */}

            <Textarea
              mt={8}
              autosize
              placeholder='Type in ...'
              label='Request to chef'
              spellCheck={false}
              value={requestToChefNote}
              onChange={(event) => setRequestToChefNote(event.currentTarget.value)}
            />

            <Flex justify='space-between' mt={12}>
              {/* {(variants.length === 0 || newCustomizationMode) && ( */}
              <Grid className={classes.foodQuantityChangerGrid}>
                <Grid.Col
                  className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerFirstGridCol)}
                  // xs={4}
                  p={0}
                  m={0}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ActionIcon
                    disabled={totalPrice.quantity === 1}
                    sx={(theme) => ({
                      ':hover': {
                        backgroundColor: 'transparent',
                      },

                      '&[data-disabled]': { pointerEvents: 'all', backgroundColor: 'transparent', border: 'none' },
                    })}
                    onClick={() => handleFoodQuantityChange('decrease', 1)}>
                    <Box>
                      <Image src={removeIcon} />
                    </Box>
                  </ActionIcon>
                </Grid.Col>
                <Grid.Col
                  className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerSecondGridCol)}
                  p={0}
                  m={0}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Text>{totalPrice.quantity}</Text>
                </Grid.Col>
                <Grid.Col
                  className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerThirdGridCol)}
                  p={0}
                  m={0}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ActionIcon
                    disabled={totalPrice.quantity === 10}
                    sx={(theme) => ({
                      ':hover': {
                        backgroundColor: 'transparent',
                      },

                      '&[data-disabled]': { pointerEvents: 'all', backgroundColor: 'transparent', border: 'none' },
                    })}
                    onClick={() => handleFoodQuantityChange('increase', 1)}>
                    <Box>
                      <Image src={addIcon} />
                    </Box>
                  </ActionIcon>
                </Grid.Col>
              </Grid>
              {/* )} */}
              {/* ------------------------------------ */}
              {/* {variants.length === 0 || newCustomizationMode ? ( */}
              {/* DEFAULT ADD (unchanged behavior)  */}
              <Button
                radius={8}
                w={160}
                styles={(theme) => ({
                  root: {
                    backgroundColor: theme.colors.red[6],
                    '&:hover': {
                      backgroundColor: theme.colors.red[5],
                    },
                  },
                })}
                loading={addToCardIsLoading}
                disabled={isLoading}
                onClick={() => {
                  handleUpdate('add');
                  setNewCustomizationMode(false);
                }}>
                ADD | $ {totalPrice.totalPrice + buildAddonMap().addonsTotal * totalPrice.quantity}
              </Button>
              {/* ) : ( */}
              {/* ADD ▾ MENU  */}
              {/* <Menu width={200} position='bottom-end' opened={showAddMenu} onChange={setShowAddMenu}>
                  <Menu.Target>
                    <Button
                      radius={8}
                      w={160}
                      styles={(theme) => ({
                        root: {
                          backgroundColor: theme.colors.red[6],
                          '&:hover': {
                            backgroundColor: theme.colors.red[5],
                          },
                        },
                      })}
                      rightIcon='▾'
                      onClick={(e) => e.stopPropagation()}>
                      ADD
                    </Button>
                  </Menu.Target>

                      '&[data-disabled]': { pointerEvents: 'all', backgroundColor: 'transparent', border: 'none' },
                    })}
                    onClick={() => handleFoodQuantityChange('decrease', 1)}>
                    <Box>
                      <Image src={removeIcon} />
                    </Box>
                  </ActionIcon>
                </Grid.Col>
                <Grid.Col
                  className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerSecondGridCol)}
                  p={0}
                  m={0}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Text>{totalPrice.quantity}</Text>
                </Grid.Col>
                <Grid.Col
                  className={cx(classes.foodQuantityChangerGenericGridCol, classes.foodQuantityChangerThirdGridCol)}
                  p={0}
                  m={0}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ActionIcon
                    disabled={totalPrice.quantity === 10}
                    sx={(theme) => ({
                      ':hover': {
                        backgroundColor: 'transparent',
                      },

                    <Menu.Item onClick={startNewCustomization}>New customization</Menu.Item>
                  </Menu.Dropdown>
                </Menu> */}
              {/* )} */}
              {/* ------------------------------------ */}
              {/* <Button
                radius={8}
                w={160}
                loading={addToCardIsLoading}
                loaderPosition='center'
                disabled={isLoading}
                onClick={handleAdd}
                styles={(theme) => ({
                  root: {
                    backgroundColor: theme.colors.red[6],
                    '&:hover': {
                      backgroundColor: theme.colors.red[5],
                    },
                  },
                })}>
                <Text lh={1}>
                  {data === undefined ? (
                    'Loading'
                  ) : (
                    <>
                      <Text lh={1}>{addToCardIsLoading ? null : `ADD | $ ${totalPrice.totalPrice.toFixed(2)}`}</Text>
                    </>
                  )}
                </Text>
              </Button> */}
            </Flex>
            {/* ---------------jan 8------------------------- */}
            {/* {variants.length > 0 && (
              <>
                <Divider my='sm' />

                <Text fw={600}>Customizations ({variants.length})</Text>

                <Table striped highlightOnHover mt='sm'>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{ textAlign: 'center' }}>Qty</th>
                      <th style={{ textAlign: 'right' }}>Unit</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => (
                      <tr
                        key={v.id}
                        style={{
                          width: '100%',
                          position: 'relative',
                        }}>
                        <td
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                          }}>
                          <Box>
                            <Text size='sm' fw={500}>
                              {Object.values(v.addons)
                                .map((a) => a.name)
                                .join(' • ') || 'Default'}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              Base ${foodInfo.price.toFixed(2)} • Addons ${v.addonsTotal.toFixed(2)}
                            </Text>
                          </Box>
                          {(_cardItemUpdateService?.isPending || _cardItemDeleteService?.isPending) &&
                            currVariantId === v.id && <Loader color='#D61D4F' size={20} ml={10} />}
                        </td>

                        <td align='center'>
                          <Group spacing={4} position='center'>
                            <ActionIcon
                              size='sm'
                              onClick={async () => {
                                await handleUpdate('decrease', {
                                  _id: v.id,
                                  addon_level_ids: v.addon_level_ids,
                                  quantity: v.quantity - 1,
                                });
                              }}>
                              −
                            </ActionIcon>

                            <Text fw={600}>{v.quantity}</Text>

                            <ActionIcon
                              size='sm'
                              onClick={async () => {
                                await handleUpdate('increase', {
                                  _id: v.id,
                                  addon_level_ids: v.addon_level_ids,
                                  quantity: v.quantity + 1,
                                });
                              }}>
                              +
                            </ActionIcon>
                          </Group>
                        </td>

                        <td align='right'>${v.unitPrice.toFixed(2)}</td>
                        <td align='right'>${(v.unitPrice * v.quantity).toFixed(2)}</td>

                        <td align='right'>
                          <ActionIcon
                            color='red'
                            onClick={async () => {
                              await handleUpdate('delete', {
                                _id: v.id,
                                addon_level_ids: v.addon_level_ids,
                                quantity: v.quantity,
                              });
                            }}>
                            🗑
                          </ActionIcon>
                        </td>
                        {(_cardItemUpdateService?.isPending || _cardItemDeleteService?.isPending) &&
                          currVariantId === v.id && (
                            <Box pos='absolute' w='100%' h='100%' top={0} left={0} bg='white' opacity={0.6} />
                          )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )} */}
            {/* --------------jan 8-------------------------- */}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {/* </Container> */}
    </>
  );
};

export default NewItemAcordion;
